import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { useWindow } from '../../contexts/WindowContext';
import { modernDarkTheme, modernLightTheme } from '../../helpers/codeHandler';
import '../../Styles/Layout/Items/CodeDisplay.css';

interface CodeDisplayProps {
  language: string;
  content: string;
  showHeader: boolean;
  wrapLines?: boolean;
  isStreaming?: boolean;
}

const CodeDisplay: React.FC<CodeDisplayProps> = ({
  language,
  content,
  showHeader = false,
  wrapLines = true,
  isStreaming = false,
}) => {
  const { resolvedTheme } = useWindow();
  const [isWrapped, setIsWrapped] = useState(wrapLines);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const activeLineNumber = content.split('\n').length;
  let renderedLineNumber = 0;

  const streamingLineProps = () => {
    renderedLineNumber += 1;
    return {
      className: `code-stream-line${renderedLineNumber === activeLineNumber ? ' is-current' : ''}`,
    };
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 1200);
    } catch {
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 1500);
    }
  };

  return (
    <div
      className={`modern-code-display ${showHeader ? '' : 'no-header'}${isStreaming ? ' is-streaming' : ''}`}
      style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}
      aria-busy={isStreaming}
    >
        {showHeader && (
          <div className="code-header" >
            <span className="code-language">{language}</span>
            <div className="code-header-actions">
              <button
                onClick={() => setIsWrapped(!isWrapped)}
                className="code-header-button"
              >
                {isWrapped ? 'Unwrap' : 'Wrap'}
              </button>
              <button
                type="button"
                onClick={copyToClipboard}
                className={`code-header-button code-copy-button ${copyState === 'copied' ? 'is-copied' : copyState === 'error' ? 'is-error' : ''}`}
                aria-label="Copy code"
                title={copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Copy failed' : 'Copy'}
              >
                <span className="code-copy-button-label">
                  {copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Failed' : 'Copy'}
                </span>
              </button>
            </div>
          </div>
        )}

      <div className="code-display-container">
        <SyntaxHighlighter
          language={language}
          wrapLines={true}
          wrapLongLines={isWrapped}
          lineProps={isStreaming ? streamingLineProps : {}}
          style={resolvedTheme === 'dark' ? modernDarkTheme as any : modernLightTheme as any}
          customStyle={{
            height: '100%',
            margin: 0,
            padding: showHeader ? '0rem 1rem 1rem 1rem' : '0 1rem 1rem 1rem',
            whiteSpace: isWrapped ? 'pre-wrap' : 'pre',
            overflowX: isWrapped ? 'hidden' : 'auto',
            width: '100%',
            fontSize: 'var(--font-size-xs)',
            borderBottomLeftRadius: showHeader ? 'var(--radius-xl)' : '0',
            borderBottomRightRadius: showHeader ? 'var(--radius-xl)' : '0',
            background: 'transparent',
          }}
          codeTagProps={{
            style: {
              tabSize: 1,
              whiteSpace: isWrapped ? 'pre-wrap' : 'pre',
              wordBreak: isWrapped ? 'break-word' : 'normal',
              overflowWrap: isWrapped ? 'break-word' : 'normal',
            }
          }}
        >
          {content}
        </SyntaxHighlighter>
      </div>
    </div>
  );
};

export default CodeDisplay;
